import React, { FC, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RootState } from 'redux/store';
import { ChartData } from 'chart.js';
import { useSelector } from 'react-redux';
import Image from 'components/image/Image';
import Button from 'components/Button/Index';
import useLocales from 'hooks/useLocales';
import Navbar from 'components/Navbar';
import Footer from 'components/Footer/Index';
import Popup from 'components/shared/Popups/Popup/Index';
import ShareJobPopup from 'components/shared/Popups/ShareJob/Index';
import CompetenciesBlock from 'components/shared/CompetenciesBlock/Index';
import { teaserCultureChart, teaserJobChart } from 'core/constants/teaserChart';
import { IJobCompetencies } from 'core/interface/job';
import { JobService } from 'services/jobs/job';
import { DegreeEnum } from 'core/enum/degree';
import { Industries } from 'core/interface/education-side';
import { ProfileService } from 'services/profile/profile';
import { getCareerLevelByValue, getSalaryByValue } from 'constants/constants';
import { CandidateService } from 'services/candidate/candidate';
import { IMe } from 'core/interface/me';
import { ICompetencies } from 'core/interface/competencyOverview';
import {
  calculateMatchPercentage,
  getMatchingScores,
  consolidateCategories
} from 'helpers/teaserView';

import './Index.scss';
import { useSnackbar } from 'notistack';
import colors from 'assets/variables/colors';

const JobDetails: FC = (): JSX.Element => {
  const { t } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const me = useSelector((state: IMe) => state.user).user;
  const navigate = useNavigate();
  const [showSharePopup, setShowSharePopup] = useState<boolean>(false);
  const [jobIndustry, setJobIndustry] = useState<string[]>([]);
  const [jobChartData, setJobChartData] = useState<ChartData>();
  const [cultureChartData, setCultureChartData] = useState<ChartData>();
  const [jobLocation, setJobLocation] = useState<string[]>([]);
  const [talentMatchPercent, setTalentMatchPercent] = useState<
    number | undefined | string
  >();
  const [cultureMatchPercent, setCultureMatchPercent] = useState<
    number | undefined | string
  >();
  const { singleJob: job } = useSelector((state: RootState) => state.jobs);

  const { industry: industries } = useSelector(
    (state: RootState) => state.industries
  );
  const { cities } = useSelector((state: RootState) => state.locations);
  const { candidatesOverview } = useSelector(
    (state: RootState) => state.candidates
  );
  const assessment = useSelector((state: RootState) => state.assessment);

  useEffect(() => {
    if (id) {
      getJobByIdOrSlug(id);
    }
  }, [id]);

  useEffect(() => {
    if (me && me.candidate_id) {
      getCandidateOverview(me.candidate_id);
    }
  }, [me]);

  useEffect(() => {
    getIndustries();
    getCities();
  }, []);

  useEffect(() => {
    if (industries?.length && job?.industries?.length) {
      getJobIndustries();
    }
  }, [industries, job]);

  useEffect(() => {
    if (cities?.length && job?.locations?.length) {
      getJobCities();
    }
  }, [cities, job]);

  const handleClose = (): void => {
    setShowSharePopup(false);
  };

  const getIndustries = (): void => {
    ProfileService.getIndustries();
  };

  const getCities = (limit: number = 20919, q?: string) => {
    JobService.getLocations({
      limit,
      q
    });
  };

  const getCandidateOverview = async (candidate_id: number): Promise<void> => {
    try {
      CandidateService.getCandidateOverview(candidate_id);
    } catch (error) {
      console.log(error);
    }
  };

  const getJobIndustries = (): void => {
    const foundIndustries: string[] = [];
    industries?.forEach((industries: Industries) => {
      job?.industries?.forEach((jobIndustries: number) => {
        if (industries.id === jobIndustries) {
          foundIndustries.push(industries.name);
          setJobIndustry(foundIndustries);
        }
      });
    });
  };

  const getProfileCompetencies = (): number[] => {
    const scores: number[] = [];
    if (candidatesOverview) {
      candidatesOverview?.competencies?.forEach(
        (candidateComp: ICompetencies) => {
          job?.competencies?.forEach((jobScores: IJobCompetencies) => {
            if (candidateComp.competency_id === jobScores.id) {
              scores.push(Number(candidateComp.score));
            }
          });
        }
      );
    }

    return scores;
  };

  const getCultureProfileCompetencies = (): number[] => {
    const scores: number[] = [];
    if (candidatesOverview) {
      candidatesOverview?.competencies?.forEach(
        (candidateComp: ICompetencies) => {
          job?.culture_profile?.competencies?.forEach(
            (jobScores: IJobCompetencies) => {
              if (candidateComp.competency_id === jobScores.competency?.id) {
                scores.push(Number(candidateComp.score));
              }
            }
          );
        }
      );
    }

    return scores;
  };

  const getJobCities = async (): Promise<void> => {
    const matchedJobs: string[] = [];
    cities?.forEach((city: any) => {
      job?.locations?.forEach((jobCity: number) => {
        if (city.id === jobCity) {
          matchedJobs.push(city.display_name);
          setJobLocation(matchedJobs);
        }
      });
    });
  };

  const getLatestAssessmentCompetencies = () => {
    let lastAssessment = null;
    for (let i = 0; i < assessment?.assessmentData?.length; i++) {
      const assessmentReport = assessment?.assessmentData[i]?.report;
      if (assessmentReport) {
        lastAssessment = assessmentReport;
        break;
      }
    }
    if (!lastAssessment) return null;
    return consolidateCategories(lastAssessment.competencies);
  };

  const getJobCompetenciesChart = (): ChartData | null => {
    if (!job?.competency_labels?.length) return null;
    const userCompetencies = getLatestAssessmentCompetencies();
    if (!userCompetencies) return null;

    const talentScores = getMatchingScores(job?.competencies, userCompetencies);
    const talentMatch = calculateMatchPercentage(
      talentScores,
      job?.competency_min_scores
    );

    if (talentMatch && talentMatch !== talentMatchPercent)
      setTalentMatchPercent(talentMatch);

    return {
      labels: job?.competency_labels,
      datasets: [
        {
          backgroundColor: 'rgba(82, 37, 131, 0.50)',
          borderColor: colors.tenantColor,
          data: job?.competency_min_scores
        },
        {
          borderColor: 'rgba(0, 158, 226, 0.50)',
          backgroundColor: '#33b1e85c',
          data: talentScores
        }
      ]
    };
  };

  const getCultureProfileCompeteciesChart = (): ChartData | null => {
    if (!job?.culture_profile?.competencies?.length) return null;
    const userCompetencies = getLatestAssessmentCompetencies();
    if (!userCompetencies) return null;

    // Filter out competencies that don't have a valid competency object
    const validCompetencies = job?.culture_profile?.competencies.filter(
      (item: any) => item?.competency?.name
    );

    if (!validCompetencies?.length) return null;

    const cultureScores = getMatchingScores(validCompetencies, userCompetencies);
    const cultureMinScores = validCompetencies.map(
      (item: any) => item.min_score
    );
    const cultureMatch = calculateMatchPercentage(
      cultureScores,
      cultureMinScores
    );

    if (cultureMatch && cultureMatch !== cultureMatchPercent)
      setCultureMatchPercent(cultureMatch);

    return {
      labels: validCompetencies.map((data: any) => data.competency.name),
      datasets: [
        {
          backgroundColor: 'rgba(82, 37, 131, 0.50)',
          borderColor: colors.tenantColor,
          data: cultureMinScores
        },
        {
          borderColor: '#33b1e8',
          backgroundColor: 'rgba(0, 158, 226, 0.50)',
          data: cultureScores
        }
      ]
    };
  };

  useEffect(() => {
    const jobData = getJobCompetenciesChart();
    if (jobData && JSON.stringify(jobData) !== JSON.stringify(jobChartData))
      setJobChartData(jobData);
    const cultureData = getCultureProfileCompeteciesChart();
    if (
      cultureData &&
      JSON.stringify(cultureData) !== JSON.stringify(cultureChartData)
    )
      setCultureChartData(cultureData);
  }, [job, assessment]);

  const handleOpenPopup = (): void => {
    setShowSharePopup(true);
  };

  const handleBackClick = (): void => {
    navigate(-1);
  };

  const getJobByIdOrSlug = (param: number | string): void => {
    JobService.getJobByIdOrSlug(param);
  };

  const handleApply = async (jobId: number) => {
    try {
      const res = await JobService.applyForAJob(jobId);
      if (res) {
        enqueueSnackbar('Applied successfully', {
          variant: 'success'
        });
      } else {
        enqueueSnackbar('Error applying for job', {
          variant: 'error'
        });
      }
    } catch (error) {
      console.error('Error applying for job:', error);
      enqueueSnackbar('Error applying for job', {
        variant: 'error'
      });
    }
  };

  return (
    <>
      <Navbar />
      <div className="jobdetails">
        <div className="jobdetails__top">
          <div onClick={handleBackClick} className="jobdetails__top__back">
            <Image name="back.svg" width={16} height={16} />
            <span className="jobdetails__top__back-overview">
              {t('Back to overview')}
            </span>
          </div>
          <Button
            onClick={handleOpenPopup}
            iconName="socials.svg"
            className="jobdetails__top-share"
            title="Share"
            t={t}
          />
        </div>
        <div className="jobdetails__about">
          <div className="jobdetails__about__sides left">
            <div className="jobdetails__about__sides__left">
              <Image
                className="jobdetails__about__sides__left-image"
                url={job?.company?.logo}
                width={100}
              />
              <div className="jobdetails__about__sides__left__content">
                <span className="jobdetails__about__sides__left__content-title">
                  {job?.title?.length > 30
                    ? ` ${job?.title.substring(0, 30)}...`
                    : job?.title}
                </span>
                <div className="jobdetails__about__sides__left__content__location">
                  <span className="jobdetails__about__sides__left__content__location-icon">
                    <Image
                      className="location"
                      name="location.svg"
                      width={12}
                      height={12}
                    />
                  </span>
                  <div className="job-side">
                    {jobLocation?.map((item, index) => (
                      <span
                        key={index}
                        className="jobdetails__about__sides__left__content__location-title"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
                <p className="jobdetails__about__sides__left__content-desc">
                  {job?.description}
                </p>
              </div>
            </div>
          </div>
          <div className="jobdetails__about__sides right">
            <div className="jobdetails__about__sides__right">
              {job?.industries && (
                <div className="jobdetails__about__sides__right__item">
                  <span className="jobdetails__about__sides__right__item__image">
                    <Image
                      className="jobdetails__about__sides__right__item__image-img"
                      name="portfolio.svg"
                      width={14}
                      height={14}
                    />
                  </span>
                  <div className="jobdetails__about__sides__right__item__content">
                    <span className="jobdetails__about__sides__right__item__content-title">
                      {t('Industries')}
                    </span>
                    {jobIndustry.map((industry, index) => (
                      <span
                        key={index}
                        className="jobdetails__about__sides__right__item__content-desc"
                      >
                        {industry}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {job?.degree && (
                <div className="jobdetails__about__sides__right__item">
                  <span className="jobdetails__about__sides__right__item__image">
                    <Image
                      className="jobdetails__about__sides__right__item__image-img"
                      name="bachelor.svg"
                      width={14}
                      height={14}
                    />
                  </span>
                  <div className="jobdetails__about__sides__right__item__content">
                    <span className="jobdetails__about__sides__right__item__content-title">
                      {t('Required Degree')}
                    </span>
                    <span className="jobdetails__about__sides__right__item__content-desc">
                      {DegreeEnum[job.degree]}
                    </span>
                  </div>
                </div>
              )}
              {job?.work_exp && (
                <div className="jobdetails__about__sides__right__item">
                  <span className="jobdetails__about__sides__right__item__image">
                    <Image
                      className="jobdetails__about__sides__right__item__image-img"
                      name="volume.svg"
                      width={14}
                      height={14}
                    />
                  </span>
                  <div className="jobdetails__about__sides__right__item__content">
                    <span className="jobdetails__about__sides__right__item__content-title">
                      {t('Career Level')}
                    </span>
                    <span className="jobdetails__about__sides__right__item__content-desc">
                      {getCareerLevelByValue(job.work_exp)}
                    </span>
                  </div>
                </div>
              )}

              {job?.salary && (
                <div className="jobdetails__about__sides__right__item">
                  <span className="jobdetails__about__sides__right__item__image">
                    <Image
                      className="jobdetails__about__sides__right__item__image-img"
                      name="salary.svg"
                      width={14}
                      height={14}
                    />
                  </span>
                  <div className="jobdetails__about__sides__right__item__content">
                    <span className="jobdetails__about__sides__right__item__content-title">
                      {t('Salary')}
                    </span>
                    <span className="jobdetails__about__sides__right__item__content-desc">
                      {getSalaryByValue(job.salary)}
                    </span>
                  </div>
                </div>
              )}

              {job?.musts && (
                <div className="jobdetails__about__sides__right__item">
                  <span className="jobdetails__about__sides__right__item__image">
                    <Image
                      className="jobdetails__about__sides__right__item__image-img"
                      name="verified.svg"
                      width={14}
                      height={14}
                    />
                  </span>
                  <div className="jobdetails__about__sides__right__item__content">
                    <span className="jobdetails__about__sides__right__item__content-title">
                      {t('Must haves')}
                    </span>
                    {job?.musts?.map((must: string, index: number) => (
                      <span
                        key={index}
                        className="jobdetails__about__sides__right__item__content-desc"
                      >
                        {must}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="jobdetails__chart">
          {job && jobChartData && (
            <CompetenciesBlock
              className="jobdetails__chart-left"
              percent={talentMatchPercent}
              chartData={jobChartData}
              chartWidth={500}
              chartHeight={500}
              title={'Match job competencies'}
              descriptions={job.competencies.map((el: any) => ({
                description: el.competency.description,
                name: el.competency.name
              }))}
              showDescription={true}
              comparePoints={jobChartData.datasets[1]?.data}
            />
          )}
          {job && cultureChartData && (
            <CompetenciesBlock
              className="jobdetails__chart-right"
              percent={cultureMatchPercent}
              title="Match culture competencies"
              chartWidth={500}
              chartHeight={500}
              careerPage={true}
              candidateId={candidatesOverview?.id}
              job={job?.company?.id}
              chartData={cultureChartData}
              descriptions={job?.culture_profile?.competencies
                .filter((el: any) => el?.competency?.name)
                .map((el: any) => ({
                  description: el.competency?.description,
                  name: el.competency?.name
                }))}
              showDescription={true}
              comparePoints={cultureChartData.datasets[1]?.data}
            />
          )}
        </div>
        <div className="jobdetails__btn">
          <div onClick={handleBackClick} className="jobdetails__top__back">
            <Image name="back.svg" width={16} height={16} />
            <span className="jobdetails__top__back-overview">
              {t('Back to overview')}
            </span>
          </div>
          <Button
            disabled={job?.is_applied}
            onClick={() => handleApply(job?.id)}
            t={t}
            className="jobdetails__btn__rigth"
            title={t('Apply')}
            tooltip={
              job?.is_applied
                ? t('You have already applied for this job')
                : undefined
            }
          />

          <Button
            onClick={() => {
              navigate(`/companies/${job?.company?.id}`);
            }}
            t={t}
            className="career_page-btn"
            title={t('View our career page')}
          />
        </div>
      </div>
      <Footer />
      {showSharePopup && (
        <Popup title="Share this job opening" onClose={handleClose}>
          <ShareJobPopup />
        </Popup>
      )}
    </>
  );
};

export default JobDetails;
